<template>
	<div class="ai-train-wrapper">
		<BackendHeader />
		<div class="ai-train-container">
			<div class="ai-train-header">
				<h1>AI Train</h1>
			</div>
		</div>
		<div>
			<div>
				Model Info: <span v-if="loadingModel">Loading...</span><span v-else>{{ model.modelName }}</span>
			</div>
		</div>
		<div class="right-left-wrapper">
			<div class="left-wrapper">
				<div class="left-wrapper-header">
					<div>
						<input type="text" v-model="postSearchText" placeholder="Search" class="post-search-input" />
					</div>
					<div>
						<button class="main-button" @click="loadPosts()" :disabled="loadingPosts">
							{{ loadingPosts ? 'LOADING...' : 'SEARCH' }}
						</button>
					</div>
          <div>
            <button class="main-button" @click="sendHeaderRequest('https://amzn.to/3HCtXEV')">TEST HEAD REQUEST</button>
            <button class="main-button" @click="sendHeaderRequest('https://mavely.app.link/e/qJQJFPEnQRb')">TEST HEAD REQUEST2</button>
          </div>
					<div class="all-the-way-to-the-right">
						<select v-model="postStatus" class="post-status-select">
							<option value="COMPLETED">PENDING</option>
							<option value="APPROVED">APPROVED</option>
							<option value="REJECTED">REJECTED</option>
						</select>
					</div>
				</div>
				<div class="table-container">
					<div class="table-header">
						<div class="table-header-item">PostId</div>
						<div class="table-header-item">CreatedAt</div>
						<div class="table-header-item">OriginalPost</div>
						<div class="table-header-item">MediaUrl</div>
						<!--<div class="table-header-item">Source</div>-->
						<div class="table-header-item">Status</div>
            <div class="table-header-item">Actions</div>
					</div>
					<div class="table-body">
						<div v-for="post in posts" :key="post.postId" class="table-row">
							<div class="table-cell">{{ post.postId }}</div>
							<div class="table-cell">{{ post.createdAt }}</div>
							<div class="table-cell trail-off-text">
								{{ post.originalPost }}
							</div>
							<div class="table-cell">
								<ImagePreview :imageUrl="post.mediaUrl" />
							</div>
							<!--<div class="table-cell">{{ post.source }}</div>-->
							<div class="table-cell">{{ post.status === 'COMPLETED' ? 'PENDING' : post.status }}</div>
							<div class="table-cell">
								<div class="buttons-wrapper">
                  <button class="main-button" @click="loadOriginalPost(post)">LOAD</button>
                  <button class="main-button" @click="loadProcessedPost(post)">LOAD AI</button>
                </div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="right-wrapper">
				<div class="train-page-wrapper">
					<div class="page-section">
						<div class="label">Paste Text</div>

						<div class="post-area">
							<!--ORIGINAL TEXT-->
							<textarea v-model="textInput" class="post-textarea" :disabled="!model"></textarea>
							<div class="ai-button">
								<button class="button" :disabled="!(textInput && modelName)" @click="useTrainingDataToFigureThisOneOut()">PROCESS USING AI</button>
							</div>
						</div>
						<!--CLEAN TEXT-->
						<textarea v-model="trainingDataItem.text" class="post-textarea" readonly style="display: none" v-if="trainingDataItem"></textarea>
					</div>

					<!--SELECT WORDS-->
					<div class="label">Select Words</div>
					<div class="train-section" @click="clearSelectedWords()" v-if="wordsArray">
						<div class="words-output">
							<div
								v-for="(w, index) in wordsArray"
								:key="index"
								class="word"
								:class="{ selected: w.isSelected, hovered: w.isHovered }"
								@click.stop="toggleWord(w)"
								@mouseenter="startHoverWord(w)"
								@mouseleave="endHoverWord()"
							>
								{{ w.word }}
							</div>
						</div>
					</div>

					<!--ANNOTATIONS SECTION-->
					<div class="label">Select Annotation</div>
					<div class="annotations" v-if="trainingDataItem">
						<div v-for="(a, index) in trainingDataItem.annotations" :key="index" :class="{ selected: a.isSelected }">
							<div class="annotation-wrapper">
								<div class="annotation" @click="toggleAnnotation(a)" :class="{ 'has-value': a.value }">
									{{ a.type }}
								</div>
								<div class="annotation-value">{{ a.value }}</div>
								<div>
									<button class="main-button" @click="clearAnnotation(a)">CLEAR</button>
								</div>
								<div>
									<button class="main-button" @click="deleteAnnotation(a)">DELETE</button>
								</div>
							</div>
						</div>
					</div>
					<div v-if="trainingDataItem">
						<button class="button submit-button" @click="submit()" :disabled="!isValid">SUBMIT</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import BackendHeader from '@/components/BackendHeader.vue';
	import { reactive, ref, computed, onMounted } from 'vue';
	import util from '@/helpers/util';
	import api from '@/api';
	import Annotation from '@/classes/Annotation';
	import TrainingDataItem from '@/classes/TrainingDataItem';
	import Post from '@/classes/Post';
	import '@/assets/backend.css';
	import ImagePreview from '@/components/Backend/ImagePreview.vue';

	interface Word {
		word: string;
		index: number;
		length: number;
		isSelected: boolean;
		isHovered: boolean;
	}

	interface Entity {
		type: string;
		value: string;
	}

	const loadingModel = ref(true);
	const modelName = ref('deal-post-model');
	const model = ref(null);
	const models = ref([]);
	const textInput = ref(null);
	const wordsArray = ref([]);
	const startingWord = ref(null);
	const endingWord = ref(null);
	const trainingDataItem = ref(null);
	const entities = ref<Entity[]>([
		{ type: 'PRODUCT_NAME', value: '' },
		{ type: 'DISCOUNT_PERCENT', value: '' },
		{ type: 'LINK', value: '' },
		{ type: 'PROMO_CODE', value: '' },
		{ type: 'RETAIL_PRICE', value: '' },
		{ type: 'SALE_PRICE', value: '' },
	]);
	const posts = ref<Post[]>([]);
	const loadingPosts = ref(false);
	const postSearchText = ref('');
	const postStatus = ref('COMPLETED');
	const currentPost = ref(null);
	const isValid = computed(() => {
		if (!trainingDataItem.value) return false;

		const hasSomeValue = trainingDataItem.value.annotations.some((a: Annotation) => !!a.value);
		if (!hasSomeValue) return false;

		const seenTypes = new Set<string>();
		let hasDuplicates = false;
		trainingDataItem.value.annotations.forEach((annotation: Annotation) => {
			if (annotation.type == null) return;

			if (seenTypes.has(annotation.type)) {
				hasDuplicates = true;
			} else {
				seenTypes.add(annotation.type);
			}
		});
		return !hasDuplicates;
	});

	onMounted(async () => {
		loadingModel.value = true;
		try {
			const modelsResponse = await api.getModels();
			models.value = modelsResponse.data;
			console.log('models', models.value);

			model.value = models.value.find((m: any) => m.modelName === modelName.value);
			console.log('model', model.value);

			if (model.value) {
				entities.value = model.value.items.map((i: any) => ({
					type: i.name,
					value: '',
				}));
			} else {
				entities.value = [];
				trainingDataItem.value = null;
				wordsArray.value = [];
			}
		} catch (error) {
			console.error('Error loading models:', error);
			alert('Failed to load models. Please ensure the Spacy service is running at https://localhost:5002');
		} finally {
			loadingModel.value = false;
		}

		//LOAD POSTS
		loadPosts();
	});

	const loadPosts = async () => {
		loadingPosts.value = true;
		const postsResponse = await api.getPosts(25, postSearchText.value, postStatus.value);
		posts.value = postsResponse.data;
		console.log('posts', posts.value);
		loadingPosts.value = false;
	};

	const toggleAnnotation = (annotation: Annotation) => {
		if (wordsArray.value.some(w => w.isSelected)) {
			const selectedWords = wordsArray.value.filter(w => w.isSelected);
			const selectedSentence = selectedWords.map(wa => wa.word).join(' ');
			const selectedConnectedPart = selectedWords.map(wa => wa.word).join('');
			if (trainingDataItem.value?.text?.includes(selectedSentence)) {
				annotation.value = selectedSentence;
			} else if (trainingDataItem.value?.text?.includes(selectedConnectedPart)) {
				annotation.value = selectedConnectedPart;
			} else {
				const sentence = trainingDataItem.value?.text;
				const firstWordStartIndex = sentence?.indexOf(selectedWords[0].word) ?? -1;
				const lastWord = selectedWords[selectedWords.length - 1].word;
				const lastWordStartIndex = sentence?.lastIndexOf(lastWord) ?? -1;
				const lastWordEndIndex = lastWordStartIndex + lastWord.length;
				annotation.value = sentence?.substring(firstWordStartIndex, lastWordEndIndex);
			}
			clearSelectedWords();
		} else {
			annotation.value = null;
		}
	};

	const clearAnnotation = (annotation: Annotation) => {
		annotation.value = null;
	};

	const deleteAnnotation = (annotation: Annotation) => {
		if (trainingDataItem.value) {
			const index = trainingDataItem.value.annotations.findIndex((a: Annotation) => a === annotation);
			if (index !== -1) trainingDataItem.value.annotations.splice(index, 1);
		}
	};

	const clearSelectedWords = () => {
		startingWord.value = null;
		endingWord.value = null;
		wordsArray.value.forEach(w => (w.isSelected = false));
	};

	const toggleWord = (word: Word) => {
		if (startingWord.value && endingWord.value) {
			startingWord.value = null;
			endingWord.value = null;
			toggleWord(word);
		} else if (startingWord.value) {
			if (word.index <= startingWord.value.index) {
				startingWord.value = null;
				endingWord.value = null;
				toggleWord(word);
			} else {
				endingWord.value = word;

				word.isSelected = true;
				wordsArray.value.filter(w => w.index >= startingWord.value!.index && w.index <= endingWord.value!.index).forEach(w => (w.isSelected = true));
				endHoverWord();
			}
		} else {
			wordsArray.value.forEach(w => (w.isSelected = false));
			startingWord.value = word;
			word.isSelected = true;
		}
	};

	const startHoverWord = (word: Word) => {
		if (startingWord.value && !endingWord.value && word.index > startingWord.value.index) {
			wordsArray.value.filter(w => w.index > startingWord.value!.index && w.index <= word.index).forEach(w => (w.isHovered = true));
		}
	};

	const endHoverWord = () => {
		wordsArray.value.forEach(w => (w.isHovered = false));
	};

	const setLabels = (labels: string[]) => {
		trainingDataItem.value!.annotations = labels.map(label => new Annotation({ type: label, value: '' }));
	};

	const useTrainingDataToFigureThisOneOut = async () => {
		breakIntoWords();

		const inputString = trainingDataItem.value?.text || '';

		const response = await api.guess(modelName.value, inputString);
		const responseData = response.data;

		console.log('responseData', responseData);

		if (responseData && responseData.labels) setLabels(responseData.labels);

		if (responseData && responseData.entities) {
			responseData.entities.forEach((g: { label: string; text: string }) => {
				const foundAnnotation = trainingDataItem.value!.annotations.find((a: Annotation) => a.type === g.label);
				if (foundAnnotation && !foundAnnotation.value) {
					foundAnnotation.value = g.text;
				} else {
					trainingDataItem.value!.annotations.push(new Annotation({ type: g.label, value: g.text }));
				}
			});
		}
	};

	const breakIntoWords = () => {
		trainingDataItem.value = new TrainingDataItem();
		trainingDataItem.value.text = util.cleanText(textInput.value || '');
		trainingDataItem.value.modelName = modelName.value;
		const text = trainingDataItem.value.text;

		const placeholders: Record<string, string> = {};
		let counter = 0;

		const replaceWithPlaceholder = (patternRegex: RegExp) => {
			text.replace(patternRegex, (match: string) => {
				const placeholder = `__PLACEHOLDER_${counter++}__`;
				placeholders[placeholder] = match;
				return placeholder;
			});
		};

		replaceWithPlaceholder(/(https?:\/\/[^\s]+)/g); //replace links
		replaceWithPlaceholder(/(\b\d+(\.\d+)?%)|(\b\d+%)/g); //replace percentages
		replaceWithPlaceholder(/(\$?\d+(\.\d{2}|\.[Xx]{2})?\b)/g); //replace prices

		const splitRegex = /(__PLACEHOLDER_\d+__|<<\d+>>|\b\w+('\w+)?\b|[^\w\s]+)/g;
		const splitText = text.match(splitRegex) || [];

		const processedText = splitText.map((token: string) => placeholders[token] || token);

		wordsArray.value = processedText.map((word: string, index: number) => ({
			word,
			index,
			length: word.length,
			isSelected: false,
			isHovered: false,
		}));
	};

	const submit = () => {
		trainingDataItem.value!.annotations = trainingDataItem.value!.annotations.filter((f: Annotation) => f.value);

		api.trainSingle(trainingDataItem.value!)
			.then(response => {
				console.log('response', response);
			})
			.catch(error => console.error('Error:', error));

		textInput.value = null;
		wordsArray.value = [];
		clearSelectedWords();
		trainingDataItem.value = null;
	};

	const loadOriginalPost = (post: Post) => {
    currentPost.value = post;
		textInput.value = post.originalPost;
	};

	const loadProcessedPost = (post: Post) => {
		
	};

  const sendHeaderRequest = async (url: string) => {
    
    let lastLocation = null;
    const maxHops = 10;
    let hops = 0;
    
    //SEND HEAD REQUESTS
    const getLocationHeader = async (url: string) => {
      hops++;
      if(hops > maxHops) return;

      const response = await api.headerRequest(url);
      
      if(response?.headers?.Location){
        lastLocation = response.headers.Location;
        await getLocationHeader(lastLocation);
      }
      else if(response?.headers?.location){
        lastLocation = response.headers.location;
        await getLocationHeader(lastLocation);
      }
      
      return;

    };

    //START HEAD REQUESTS
    await getLocationHeader(url);
    
    console.log('lastLocation', lastLocation);

  };

</script>

<style>
	.label {
		text-align: left;
	}

	.train-page-wrapper {
		display: flex;
		flex-direction: column;
		gap: 10px;
		align-items: flex-start;
		justify-content: flex-start;
	}

	.page-section {
		width: 100%;
		text-align: left;
	}

	.post-textarea {
		height: 100px;
	}

	.train-section {
		border: 3px solid #000;
		border-radius: 15px;
		padding: 15px;
		display: grid;
		gap: 5px;
		grid-template-columns: 1fr;
	}

	.words-output {
		display: flex;
		flex-wrap: wrap;
	}

	.word {
		margin-right: 10px;
		margin-bottom: 10px;
		padding: 5px;
		background-color: #f0f0f0;
		border-radius: 5px 10px;
		cursor: pointer;
		transition: background-color 0.25s ease;
	}

	.word:hover {
		background-color: #999999;
	}

	.word.selected {
		background-color: red;
	}

	.word.hovered {
		background-color: rgb(240, 121, 121);
	}

	.annotations {
		display: grid;
		gap: 10px;
		width: 100%;
	}

	.annotation-wrapper {
		display: grid;
		gap: 5px;
		grid-template-columns: 215px 3fr 85px 85px;
	}

	.annotation {
		padding: 5px;
		background-color: #f0f0f0;
		border-radius: 5px;
		height: 2em;
		cursor: pointer;
		display: grid;
		align-items: center;
		justify-content: center;
	}

	.annotation.has-value {
		background-color: #2ec520;
		color: white;
	}

	.annotation:hover {
		background-color: #999999;
	}

	.annotation-value {
		text-align: left;
		font-size: 12px;
		display: flex;
		align-items: center;
	}

	.annotation.selected {
		background-color: red;
	}

	button.main-button {
		background-color: #3342d1;
		color: white;
		padding: 10px;
	}

	button.submit-button {
		width: 150px;
		margin-top: 25px;
	}

	.ai-button {
		width: 100%;
		display: flex;
		justify-content: flex-end;
	}

	.ai-button button {
		width: 100px;
	}

	.post-area {
		display: grid;
		grid-template-columns: 1fr 150px;
		width: 100%;
	}

	.right-left-wrapper {
		display: grid;
		grid-template-columns: 1fr 1fr;
		width: 100%;
	}

	.left-wrapper-header {
		width: 100%;
		display: flex;
		justify-content: flex-start;
		margin-bottom: 10px;
	}

	.all-the-way-to-the-right {
		margin-left: auto;
	}

	.left-wrapper {
		border: 1px solid #000;
		padding: 10px;
	}

	.right-wrapper {
		padding: 10px;
		border: 1px solid #000;
	}

	.trail-off-text {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		max-width: 200px;
	}

	button {
		cursor: pointer;
	}

	.post-search-input {
		width: 200px;
		margin-right: 10px;
		height: 25px;
		border-radius: 5px;
		border: 1px solid #7e7e7e;
		padding: 5px;
	}

	.post-status-select {
		height: 35px;
		border-radius: 5px;
		border: 1px solid #7e7e7e;
		padding: 5px;
	}

	.buttons-wrapper {
		display: flex;
		gap: 10px;
	}
</style>
